import { IMainMenuData, IMainMenuAction } from 'models/mainmenu/mainmenu';
import { IStore } from '../../../IStore';
import { IMenuItemListItem } from 'models/menuitem/menuitemlistitem';
import { MenuItemType } from 'models/enums/menuitemtype';
import { IMainMenuSelectedItem } from 'models/common';

/** Action Types */
export const REQUEST_START: string = 'mainmenu/REQUEST_START';
export const REQUEST_FAILURE: string = 'mainmenu/REQUEST_FAILURE';
export const RESET_DATA: string = 'mainmenu/RESET_DATA';
export const GET_MAIN_MENU_ITEMS_SUCCESS: string = 'mainmenu/GET_MAIN_MENU_ITEMS_SUCCESS';
export const SET_SELECTED_ITEMS: string = 'mainmenu/SET_SELECTED_ITEMS';
export const SET_CAN_WRITE: string = 'mainmenu/SET_CAN_WRITE';
export const SET_MENU_ITEM_TYPE: string = 'mainmenu/SET_MENU_ITEM_TYPE';

/** Initial State */
const initialState: IMainMenuData = {
	portalItems: [],
	magazineItems: [],
	selectedItems: [],
	isFetching: false,
	canWrite: true,
	menuItemType: null,
};

/** Reducer */
export function mainMenuReducer(state = initialState, action: IMainMenuAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case RESET_DATA:
			return Object.assign({}, state, {
				portalItems: [],
				magazineItems: action.payload?.menuItemType === MenuItemType.Portal ? state.magazineItems : [],
				selectedItems: action.payload?.menuItemType === MenuItemType.Magazine ? state.selectedItems : [],
				isFetching: false,
				canWrite: true,
				menuItemType: null,
			});

		case GET_MAIN_MENU_ITEMS_SUCCESS:
			return Object.assign({}, state, {
				[action.payload.menuItemType === MenuItemType.Magazine ? 'magazineItems' : 'portalItems']: action.payload.items,
			});

		case SET_SELECTED_ITEMS:
			return Object.assign({}, state, {
				selectedItems: action.payload.selectedItems,
			});

		case SET_CAN_WRITE:
			return Object.assign({}, state, {
				canWrite: action.payload.canWrite,
			});

		case SET_MENU_ITEM_TYPE:
			return Object.assign({}, state, {
				menuItemType: action.payload.menuItemType,
			});

		default:
			return state;
	}
}

/** Action Creator */
export function getPublishedMenuItems(menuItemType: MenuItemType) {
	return (dispatch, getState: () => IStore) => {
		if (!getState().mainMenu.menuItemType) {
			dispatch(setMenuItemType(menuItemType));
		}

		dispatch(requestStart());
		//dispatch(resetData(menuItemType));

		const language = getState().userLanguage.languageKey;
		let url = 'api/MenuItem';
		if (menuItemType === MenuItemType.Portal) {
			url += '/Portal/Published';
		} else if (menuItemType === MenuItemType.Magazine) {
			url += '/Magazine/Published';
		}

		if (language) {
			url += '?language=' + language;
		}

		return fetch(getState().fetchDefault.baseAPI + url, Object.assign({}, getState().fetchDefault.requestInit))
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IMenuItemListItem[]) => {
						res.map((item: IMenuItemListItem) => {
							item.pageJObject = JSON.parse(item.page);
						});
						res.sort((a, b) => a.sortOrder - b.sortOrder);
						dispatch(getMainMenuItemsSuccess(res, menuItemType));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function setSelectedItem(key: number, value: string, parentKey?: string) {
	return (dispatch, getState: () => IStore) => {
		const newArrayOfSelectedItems: IMainMenuSelectedItem[] = [];
		getState().mainMenu.selectedItems.forEach((i) => {
			newArrayOfSelectedItems.push({ ...i });
		});
		const indexToUpdate = newArrayOfSelectedItems.findIndex((x) => x.key === key);
		newArrayOfSelectedItems[indexToUpdate].value = value;

		if (parentKey !== undefined) {
			newArrayOfSelectedItems[indexToUpdate].parentKey = parentKey;
		}

		dispatch(setSelectedItems(newArrayOfSelectedItems));
	};
}

/** Action Creator */
export function setParentKey(key: number, parentKey: string) {
	return (dispatch, getState: () => IStore) => {
		const newArrayOfSelectedItems: IMainMenuSelectedItem[] = [];
		getState().mainMenu.selectedItems.forEach((i) => {
			newArrayOfSelectedItems.push({ ...i });
		});
		const indexToUpdate = newArrayOfSelectedItems.findIndex((x) => x.key === key);
		newArrayOfSelectedItems[indexToUpdate].parentKey = parentKey;
		dispatch(setSelectedItems(newArrayOfSelectedItems));
	};
}

/** Action Creator */
export function requestStart(): IMainMenuAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IMainMenuAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function resetData(menuItemType: MenuItemType): IMainMenuAction {
	return {
		type: RESET_DATA,
		payload: {
			menuItemType,
		},
	};
}

/** Action Creator */
export function getMainMenuItemsSuccess(items: IMenuItemListItem[], menuItemType: MenuItemType): IMainMenuAction {
	return {
		type: GET_MAIN_MENU_ITEMS_SUCCESS,
		payload: {
			items,
			menuItemType,
		},
	};
}

/** Action Creator */
export function setSelectedItems(selectedItems: IMainMenuSelectedItem[]): IMainMenuAction {
	return {
		type: SET_SELECTED_ITEMS,
		payload: {
			selectedItems,
		},
	};
}

/** Action Creator */
export function setCanWrite(canWrite: boolean): IMainMenuAction {
	return {
		type: SET_CAN_WRITE,
		payload: {
			canWrite,
		},
	};
}

/** Action Creator */
export function setMenuItemType(menuItemType: MenuItemType): IMainMenuAction {
	return {
		type: SET_MENU_ITEM_TYPE,
		payload: {
			menuItemType,
		},
	};
}
